import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { StateChoroplethMap } from './StateChoroplethMap';
import { ScenarioCategoryHeatmap } from './ScenarioCategoryHeatmap';
import type { StateScenarioCategoryStat, StateScenarioOverallStat } from '@/hooks/useStateScenarioStats';

interface StateScenarioViewProps {
  categoryStats: StateScenarioCategoryStat[];
  overallStats: StateScenarioOverallStat[];
  categories: string[];
  selectedState: string | null;
  onStateSelect: (state: string | null) => void;
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

export function StateScenarioView({
  categoryStats,
  overallStats,
  categories,
  selectedState,
  onStateSelect,
}: StateScenarioViewProps) {
  const { t } = useTranslation(['rankings', 'common']);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  const [mapCategory, setMapCategory] = useState<string>('all');

  const cardBg = isAhp
    ? (isDark ? 'rgba(10,22,40,0.85)' : 'rgba(255,255,255,0.95)')
    : 'rgba(0,0,0,0.85)';
  const borderColor = isAhp
    ? (isDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,0,0,0.1)')
    : 'rgba(255,255,255,0.1)';
  const textColor = isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : '#fff';
  const mutedText = isAhp ? (isDark ? 'rgba(250,249,246,0.6)' : 'rgba(0,0,0,0.5)') : 'rgba(255,255,255,0.5)';

  // Map data: either overall or filtered by category
  const mapData = useMemo(() => {
    const m: Record<string, number> = {};
    if (mapCategory === 'all') {
      for (const row of overallStats) {
        m[row.state] = Number(row.median_markup_pct);
      }
    } else {
      for (const row of categoryStats) {
        if (row.category === mapCategory) {
          m[row.state] = Number(row.median_markup_pct);
        }
      }
    }
    return m;
  }, [overallStats, categoryStats, mapCategory]);

  // Bar chart data for selected state: category breakdown vs national median
  const stateBarData = useMemo(() => {
    if (!selectedState) return [];

    // National medians per category
    const nationalByCat: Record<string, number[]> = {};
    for (const row of categoryStats) {
      if (!nationalByCat[row.category]) nationalByCat[row.category] = [];
      nationalByCat[row.category].push(Number(row.median_markup_pct));
    }
    const nationalMedians: Record<string, number> = {};
    for (const [cat, vals] of Object.entries(nationalByCat)) {
      const sorted = [...vals].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      nationalMedians[cat] = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }

    // This state's category data
    return categories.map((cat) => {
      const stateRow = categoryStats.find(r => r.state === selectedState && r.category === cat);
      const shortLabel = cat.split(' ')[0].replace(/&$/, '').trim();
      return {
        category: shortLabel.length > 10 ? shortLabel.slice(0, 10) + '..' : shortLabel,
        fullCategory: cat,
        stateMarkup: stateRow ? Number(stateRow.median_markup_pct) : 0,
        nationalMarkup: nationalMedians[cat] ?? 0,
      };
    }).sort((a, b) => b.stateMarkup - a.stateMarkup);
  }, [selectedState, categoryStats, categories]);

  return (
    <div className="space-y-6">
      {/* Map with category filter */}
      <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="text-sm font-medium" style={{ color: textColor }}>{t('stateComparison.scenario.mapBy')}</span>
          <select
            value={mapCategory}
            onChange={(e) => setMapCategory(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-lg outline-none"
            style={{
              backgroundColor: isAhp ? (isDark ? 'rgba(0,20,60,0.8)' : 'rgba(0,0,0,0.05)') : 'rgba(255,255,255,0.1)',
              color: textColor,
              border: `1px solid ${borderColor}`,
            }}
          >
            <option value="all">{t('stateComparison.scenario.allCategories')}</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <StateChoroplethMap
          data={mapData}
          selectedState={selectedState}
          onStateClick={(s) => onStateSelect(s === selectedState ? null : s)}
          valueLabel={mapCategory === 'all' ? t('stateComparison.scenario.overallMedianMarkup') : t('stateComparison.scenario.categoryMarkup', { category: mapCategory })}
        />
      </div>

      {/* Heatmap matrix */}
      <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
        <h3 className="text-sm font-semibold mb-3" style={{ color: textColor }}>
          {t('stateComparison.scenario.heatmapTitle')}
        </h3>
        <ScenarioCategoryHeatmap
          categoryStats={categoryStats}
          categories={categories}
          selectedState={selectedState}
          onStateClick={(s) => onStateSelect(s === selectedState ? null : s)}
        />
      </div>

      {/* State detail bar chart */}
      {selectedState && stateBarData.length > 0 && (
        <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
          <h3 className="text-sm font-semibold mb-1" style={{ color: textColor }}>
            {t('stateComparison.scenario.categoryMarkupVsNational', { state: STATE_NAMES[selectedState] || selectedState })}
          </h3>
          <div className="text-xs mb-3" style={{ color: mutedText }}>
            {t('stateComparison.scenario.barsDescription')}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mb-3 text-xs">
            <span className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: isAhp ? (isDark ? '#7da6d4' : '#002868') : '#3b82f6' }} />
              <span style={{ color: mutedText }}>{selectedState}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 border-t-2 border-dashed" style={{ borderColor: isAhp ? (isDark ? '#e85d6c' : '#B22234') : '#ef4444', width: '12px' }} />
              <span style={{ color: mutedText }}>{t('stateComparison.scenario.nationalMedian')}</span>
            </span>
          </div>

          <ResponsiveContainer width="100%" height={Math.max(300, stateBarData.length * 28)}>
            <BarChart data={stateBarData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isAhp ? (isDark ? 'rgba(197,165,90,0.1)' : '#e5e7eb') : 'rgba(255,255,255,0.08)'}
                horizontal={false}
              />
              <XAxis
                type="number"
                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v}%`}
                stroke={isAhp ? (isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af') : 'rgba(255,255,255,0.3)'}
                fontSize={10}
              />
              <YAxis
                type="category"
                dataKey="category"
                width={80}
                stroke={isAhp ? (isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af') : 'rgba(255,255,255,0.3)'}
                fontSize={10}
                tick={{ fill: isAhp ? (isDark ? 'rgba(255,255,255,0.7)' : '#374151') : 'rgba(255,255,255,0.6)' }}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value >= 0 ? '+' : ''}${Number(value).toFixed(1)}%`,
                  name === 'stateMarkup' ? selectedState : 'National',
                ]}
                contentStyle={{
                  backgroundColor: isAhp ? (isDark ? '#0f1d2f' : '#fff') : '#1a1a2e',
                  border: `1px solid ${borderColor}`,
                  borderRadius: 8,
                  fontSize: 12,
                  color: textColor,
                }}
              />
              <ReferenceLine x={0} stroke={isAhp ? (isDark ? 'rgba(255,255,255,0.2)' : '#ccc') : 'rgba(255,255,255,0.2)'} />
              <Bar
                dataKey="stateMarkup"
                fill={isAhp ? (isDark ? '#7da6d4' : '#002868') : '#3b82f6'}
                radius={[0, 4, 4, 0]}
                barSize={16}
              />
              <Bar
                dataKey="nationalMarkup"
                fill="none"
                stroke={isAhp ? (isDark ? '#e85d6c' : '#B22234') : '#ef4444'}
                strokeWidth={2}
                strokeDasharray="4 3"
                barSize={16}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
